/**
 * Basic tablist functionality with native js.
 *
 * Triggers following custom events on given tablist element:
 * - `tabs.change`: Triggered after active tab has changed. Event details is object with active tab id and previously active tab id.
 * - `tabs.init`: Triggered after tab has been initialized. Triggered once after initialization.
 * - `tabs.reinit`: Triggered after reinitialization is completed. Reinitialization is triggered manually with `reinit` method. Reinit must be called after tab element is added or removed.
 *
 * @example <caption>Minimum required html</caption>
 *
 * <div role="tablist">
 *   <button id="first-tab" type="button" role="tab" aria-controls="first-panel" aria-selected="true">First tab</button>
 *   <button id="second-tab" type="button" role="tab" aria-controls="second-panel">Second tab</button>
 * </div>
 *
 * <div id="first-panel" aria-labelledby="first-tab" role="tabpanel" tabindex="0">First panel content</div>
 * <div id="second-panel" aria-labelledby="second-tab" role="tabpanel" tabindex="0" hidden>Second panel content</div>
 */
class Tabs {

  /**
   * Adds tab functionality to given tablist.
   *
   * @param {HTMLElement} element Element with [role="tablist"].
   */
  constructor(element) {
    this.tablistElement = element;
    this.tabs = [];

    this._clickEventListener = this._handleTabClickEvent.bind(this);
    this._keyEventListener = this._handleTabKeyEvent.bind(this);
    this.reinit();
    this._init = true;
  }

  _handleTabClickEvent(e) {
    this.showTab(e.target.closest('[role="tab"]').id);
  }

  _handleTabKeyEvent(e) {
    const isVertical = this.tablistElement.getAttribute('aria-orientation') === 'vertical';
    const activeTabs = this._getActiveTabs();
    if (activeTabs.length === 0) {
      return;
    }

    const oldIndex = activeTabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true');
    if (e.key === 'ArrowDown' && isVertical || e.key === 'ArrowRight') {
      this.showTab(activeTabs[oldIndex < activeTabs.length - 1 ? oldIndex + 1 : 0].id);
    }
    else if (e.key === 'ArrowUp' && isVertical || e.key === 'ArrowLeft') {
      this.showTab(activeTabs[oldIndex - 1 >= 0 ? oldIndex - 1 : activeTabs.length - 1].id);
    }
    else if (e.key === 'Home') {
      this.showTab(activeTabs[0].id);
    }
    else if (e.key === 'End') {
      this.showTab(activeTabs[activeTabs.length - 1].id);
    }
  }

  _trigger(type, data) {
    this.tablistElement.dispatchEvent(new CustomEvent('tabs.' + type, { detail: data }));
  }

  /**
   * Add event listener to tab container.
   *
   * @param {string} name Event name without `tabs.` prefix.
   * @param {function} listener Event callback.
   */
  on(name, listener) {
    this.tablistElement.addEventListener('tabs.' + name, listener);
  }

  /**
   * Updates dom references and removes/attaches listeners.
   */
  reinit() {
    this.tabs.forEach(tab => {
      tab.removeEventListener('click', this._clickEventListener);
      tab.removeEventListener('keyup', this._keyEventListener);
    });
    this.tabs = Array.from(this.tablistElement.querySelectorAll('[role="tab"]'));
    this.tabs.forEach(tab => {
      tab.addEventListener('click', this._clickEventListener);
      tab.addEventListener('keyup', this._keyEventListener);
    });

    const firstActiveTab = this._getActiveTabs().at(0) || null;
    const selectedTab = this.getSelectedTab();
    const tab = selectedTab || firstActiveTab;
    this._updateTabs(tab?.id);

    this._trigger(this._init ? 'reinit' : 'init', null);
  }

  /**
   * Get currently selected tab.
   *
   * @returns {HTMLElement|null}
   */
  getSelectedTab() {
    return this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || null;
  }

  /**
   * Get active tabs.
   *
   * @returns {HTMLElement[]} Array of active tabs.
   * @private
   */
  _getActiveTabs() {
    return this.tabs.filter(tab => !tab.disabled);
  }

  /**
   * Updates tab attributes and visibility.
   *
   * @param {string} tabId Active tab id.
   * @private
   */
  _updateTabs(tabId) {
    this.tabs.forEach(tab => {
      const isActiveTab = tab.id === tabId;
      tab.setAttribute('aria-selected', isActiveTab ? 'true' : 'false');
      tab.setAttribute('tabindex', isActiveTab ? '0' : '-1');
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) {
        panel.hidden = !isActiveTab;
      }
    });
  }

  /**
   * Selects given tab.
   *
   * @param {string} id Tab html id.
   */
  showTab(id) {
    const selectedTabId = this.getSelectedTab()?.id;
    const newSelectedTab = this._getActiveTabs().find(tab => tab.id === id);
    if (selectedTabId === id || !newSelectedTab) {
      return;
    }

    this._updateTabs(id);

    newSelectedTab.focus();

    this._trigger('change', { tabId: id, oldTabId: selectedTabId });
  }

}

export default Tabs;
